import {useContext, useState} from "react";
import {Query, QueryResult} from "@material-table/core";
import {UserWithRoleSchema} from "@feature/types";
import useAsyncError from "@hooks/useAsyncError";
import {buildFilterFromQuery, MTColumnsType, orderingFromQuery} from "@utils/pagination";
import {NotificationsContext} from "@ui-components/Notifications";
import _ from "lodash";
import apiRequest from "@services/apiRequest";

interface PaginatedResultSchema {
  items: UserWithRoleSchema[];
  total: number;
}

export const useUsers = (country: string) => {

  // context
  const {push} = useContext(NotificationsContext);

  // hooks
  const throwError = useAsyncError();

  // state
  const [loading, setLoading] = useState(false);

  // utilities
  const upsertErrorHandler = (e: any) => {
    let message: string | undefined;
    if (e.response?.status !== 422)
      message = "malformed_data_error";
    else if (e.response?.data.detail === "user_already_registered")
      message = "user_already_registered";
    // if a message is set, show it and return false in order to avoid rethrowing the error...
    if (message) {
      push({type: "error", title: message});
      return false;
    }
    // ...otherwise return true in order to rethrow it
    return true;
  }
  const baseHandler = async <T>(
    callback: () => Promise<T>,
    onSuccess?: () => void,
    onError?: (e: any) => boolean,
  ): Promise<T | null> => {
    setLoading(true);
    try {
      const result = await callback();
      onSuccess?.();
      return result;
    }
    catch (e: any) {
      const rethrow = !!onError ? onError(e) : true;
      if (rethrow)
        throwError(e);
      return null;
    }
    finally {
      setLoading(false);
    }
  }

  // handlers
  const fetch = async (
    query: Query<UserWithRoleSchema>,
    columns: MTColumnsType<UserWithRoleSchema>,
  ) => {
    const callback = async () => {
      const payload = {
        country: country,
        filters: (query && buildFilterFromQuery(query, columns)) || null,
        ordering: (query && orderingFromQuery(query)) || null,
        offset: query!.page * query!.pageSize,
        limit: query!.pageSize,
      };
      const {items, total}: PaginatedResultSchema = await apiRequest.post("/users/all", payload);
      return {data: items, totalCount: total, page: query!.page} as QueryResult<UserWithRoleSchema>;
    };
    return await baseHandler<QueryResult<UserWithRoleSchema>>(callback);
  };
  const add = async (data: UserWithRoleSchema) => {
    const callback = async () => {
      return (await apiRequest.post("/users", data)) as UserWithRoleSchema;
    };
    return await baseHandler<UserWithRoleSchema>(
      callback,
      () => {
        push({type: "success", title: "user_created_successfully"});
      },
      upsertErrorHandler,
    );
  }
  const update = async (newData: UserWithRoleSchema, oldData?: UserWithRoleSchema) => {
    const callback = async () => {
      if (!oldData || _.isEqual(newData, oldData))
        return null;
      return (await apiRequest.post(`/users/${oldData.id}`, newData)) as UserWithRoleSchema;
    };
    return await baseHandler<UserWithRoleSchema | null>(
      callback,
      () => {
        push({type: "success", title: "user_updated_successfully"});
      },
      upsertErrorHandler,
    );
  }
  const remove = async (uid: number) => {
    const callback = async () => {
      await apiRequest.delete(`/users/${uid}`);
    }
    return await baseHandler<void>(
      callback,
      () => {
        push({type: "success", title: "user_deleted_successfully"});
      }
    );
  }
  const fetchInternals = async () => {
    const callback = async () => {
      return (await apiRequest.get("/users/internal")) as UserWithRoleSchema[];
    };
    return await baseHandler<UserWithRoleSchema[]>(callback);
  }

  return {fetch, fetchInternals, add, update, remove, loading};
}